/*
  Carla Moda — small progressive-enhancement components.
  Every element works without JS (native <details>, scroll-snap tracks, forms);
  this file only adds controls, timers and cart-drawer upsell handling.
*/
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     <cm-countdown data-end="2026-12-31T23:59:59" data-minutes="10" data-key="cart">
     Renders hh:mm:ss (or dd:hh:mm:ss when > 48h). Hides itself when done.
     --------------------------------------------------------------------- */
  if (!customElements.get('cm-countdown')) {
    customElements.define(
      'cm-countdown',
      class CmCountdown extends HTMLElement {
        connectedCallback() {
          this.end = this.resolveEnd();
          if (!this.end) {
            this.hidden = true;
            return;
          }
          this.units = {};
          ['d', 'h', 'm', 's'].forEach((u) => (this.units[u] = this.querySelector(`[data-unit="${u}"]`)));
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }
        disconnectedCallback() {
          clearInterval(this.timer);
        }
        resolveEnd() {
          if (this.dataset.daily !== undefined) {
            const d = new Date();
            d.setHours(24, 0, 0, 0);
            return d.getTime();
          }
          if (this.dataset.end) {
            const t = Date.parse(this.dataset.end);
            return isNaN(t) ? null : t;
          }
          if (this.dataset.minutes) {
            const key = `cm-countdown-${this.dataset.key || 'default'}`;
            let stored = null;
            try {
              stored = sessionStorage.getItem(key);
            } catch (e) {
              stored = null;
            }
            let end = stored ? parseInt(stored, 10) : NaN;
            if (!end || end < Date.now()) {
              end = Date.now() + parseFloat(this.dataset.minutes) * 60000;
              try {
                sessionStorage.setItem(key, String(end));
              } catch (e) {
                /* storage unavailable: run in-memory */
              }
            }
            return end;
          }
          return null;
        }
        tick() {
          let diff = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
          if (diff === 0) {
            clearInterval(this.timer);
            const wrap = this.closest('[data-countdown-wrap]') || this;
            wrap.hidden = true;
            return;
          }
          const d = Math.floor(diff / 86400);
          diff -= d * 86400;
          const h = Math.floor(diff / 3600);
          diff -= h * 3600;
          const m = Math.floor(diff / 60);
          const s = diff - m * 60;
          const showDays = d > 0 && this.units.d;
          if (this.units.d) this.units.d.parentElement.hidden = !showDays;
          this.set('d', d);
          this.set('h', showDays ? h : h + d * 24);
          this.set('m', m);
          this.set('s', s);
        }
        set(u, v) {
          if (this.units[u]) this.units[u].textContent = String(v).padStart(2, '0');
        }
      }
    );
  }

  /* ---------------------------------------------------------------------
     <cm-slider> wraps .cm-slider__track + [data-prev]/[data-next]/[data-dots]
     --------------------------------------------------------------------- */
  if (!customElements.get('cm-slider')) {
    customElements.define(
      'cm-slider',
      class CmSlider extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.cm-slider__track');
          if (!this.track) return;
          this.slides = Array.from(this.track.children);
          this.prev = this.querySelector('[data-prev]');
          this.next = this.querySelector('[data-next]');
          this.dots = this.querySelector('[data-dots]');
          if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
          if (this.next) this.next.addEventListener('click', () => this.step(1));
          if (this.dots) this.buildDots();
          this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
          window.addEventListener('resize', () => this.onScroll(), { passive: true });
          this.onScroll();
        }
        slideWidth() {
          if (this.slides.length < 2) return this.track.clientWidth;
          return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
        }
        currentIndex() {
          const w = this.slideWidth() || 1;
          return Math.round(this.track.scrollLeft / w);
        }
        step(dir) {
          const target = Math.min(this.slides.length - 1, Math.max(0, this.currentIndex() + dir));
          this.goTo(target);
        }
        goTo(i) {
          const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.track.scrollTo({ left: i * this.slideWidth(), behavior: reduce || document.hidden ? 'auto' : 'smooth' });
        }
        buildDots() {
          this.dots.innerHTML = '';
          this.slides.forEach((_, i) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'cm-slider__dot';
            b.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
            b.addEventListener('click', () => this.goTo(i));
            this.dots.appendChild(b);
          });
        }
        onScroll() {
          const i = this.currentIndex();
          const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
          if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
          if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll;
          if (this.dots) {
            Array.from(this.dots.children).forEach((d, j) => d.setAttribute('aria-current', j === i ? 'true' : 'false'));
          }
        }
      }
    );
  }

  /* ---------------------------------------------------------------------
     <cm-faq-tabs> buttons[data-filter] toggle .cm-faq__group[data-group]
     --------------------------------------------------------------------- */
  if (!customElements.get('cm-faq-tabs')) {
    customElements.define(
      'cm-faq-tabs',
      class CmFaqTabs extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.from(this.querySelectorAll('[data-filter]'));
          const root = this.closest('.cm-faq') || document;
          this.groups = Array.from(root.querySelectorAll('.cm-faq__group[data-group]'));
          this.tabs.forEach((t) => t.addEventListener('click', () => this.select(t.dataset.filter)));
        }
        select(key) {
          this.tabs.forEach((t) => t.setAttribute('aria-selected', t.dataset.filter === key ? 'true' : 'false'));
          this.groups.forEach((g) => g.classList.toggle('is-hidden', key !== 'all' && g.dataset.group !== key));
        }
      }
    );
  }

  /* ---------------------------------------------------------------------
     <cm-video> click-to-play, pauses siblings
     --------------------------------------------------------------------- */
  if (!customElements.get('cm-video')) {
    customElements.define(
      'cm-video',
      class CmVideo extends HTMLElement {
        connectedCallback() {
          this.video = this.querySelector('video');
          this.button = this.querySelector('.cm-video__play');
          if (!this.video || !this.button) return;
          this.button.addEventListener('click', () => this.play());
          this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
          this.video.addEventListener('ended', () => this.classList.remove('is-playing'));
        }
        play() {
          document.querySelectorAll('cm-video video').forEach((v) => {
            if (v !== this.video) v.pause();
          });
          this.video.muted = false;
          this.video.controls = true;
          const p = this.video.play();
          if (p && p.catch) p.catch(() => {});
          this.classList.add('is-playing');
        }
      }
    );
  }

  /* ---------------------------------------------------------------------
     <cm-cart-upsell> inside the cart drawer: select variant + add
     Reuses Dawn's cart-drawer.renderContents with section rendering.
     --------------------------------------------------------------------- */
  if (!customElements.get('cm-cart-upsell')) {
    customElements.define(
      'cm-cart-upsell',
      class CmCartUpsell extends HTMLElement {
        connectedCallback() {
          this.select = this.querySelector('select');
          this.button = this.querySelector('button');
          this.error = this.querySelector('[data-error]');
          if (!this.button) return;
          this.button.addEventListener('click', (e) => {
            e.preventDefault();
            this.add();
          });
        }
        variantId() {
          if (this.select) return this.select.value;
          return this.button.dataset.variantId;
        }
        add() {
          const id = this.variantId();
          if (!id) return;
          this.button.disabled = true;
          this.button.classList.add('loading');
          const drawer = document.querySelector('cart-drawer');
          const sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map((s) => s.id) : ['cart-drawer', 'cart-icon-bubble'];
          const body = JSON.stringify({
            items: [{ id: parseInt(id, 10), quantity: 1 }],
            sections: sections,
            sections_url: window.location.pathname,
          });
          const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          };
          fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}`, { ...config, body })
            .then((r) => r.json())
            .then((response) => {
              if (response.status) {
                if (this.error) {
                  this.error.textContent = response.description || response.message || '';
                  this.error.hidden = false;
                }
                return;
              }
              if (drawer && drawer.renderContents) {
                drawer.renderContents(response);
              } else {
                window.location.href = window.routes ? window.routes.cart_url : '/cart';
              }
              if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cm-cart-upsell', cartData: response });
              }
            })
            .catch(() => {
              if (this.error) {
                this.error.textContent = window.cartStrings ? window.cartStrings.error : 'Something went wrong.';
                this.error.hidden = false;
              }
            })
            .finally(() => {
              this.button.disabled = false;
              this.button.classList.remove('loading');
            });
        }
      }
    );
  }
})();
